import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import List from '../../controls/List.jsx';
import NotificationDetails from './NotificationDetails.jsx';
import NotificationTemplate from '../../../services/notificationTemplate.js';
import PubSub from '../../../services/PubSub.js';
import Toolbar from '../../controls/Toolbar.jsx';
import CreateNotificationTemplateModal from './CreateNotificationTemplateModal.jsx';
import { useHasPermission } from '../../../common/Hooks.jsx';
import Utils from '../../../utils/utils.js';

const SendNotifications = ({
  userIds,
}) => {
  const { objectType, id, objectSubType, objectSubTypeId } = useParams();

  const templatePermissions = useHasPermission({
    domain: Utils.getDomain(),
    environment: null,
    deployment: id,
    type: 'notificationTemplate',
  });

  const permissions = useHasPermission({
    domain: Utils.getDomain(),
    environment: null,
    deployment: id,
    type: 'notification',
  });

  const history = useHistory();
  const [translate] = useTranslation();

  const [notificationsParams, setNotificationsParams] = useState({ pageIncrement: 0 });
  const [cursors, setCursors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showCreateNotification, setShowCreateNotification] = useState(false);

  const [data, setData] = useState([]);

  const baseUrl = `/${objectType}/${id}/${objectSubType}`;

  const showNotificationTemplate = useCallback((notificationTemplateId) => {
    let page = `/notifications/${id}/send`;
    if (notificationTemplateId) {
      page += `/${notificationTemplateId}`;
    }
    history.push(page);
  }, [history, id]);


  const getNotificationTemplates = useCallback(async (params) => {
    const response = await NotificationTemplate.getAllNotificationTemplate(id, null, params.firstCursor, params.lastCursor, params.pageIncrement < 0, params.getLatest);
    if (response.data) {
      setData(response.data);

      setCurrentPage(page => {
        const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : params.entryDeleted ? 1 : page + params.pageIncrement;
        setHasNextPage(response.paging.count > result * response.paging.pageSize);
        return result;
      });
      setCursors({
        firstCursor: response.paging.firstCursor,
        lastCursor: response.paging.lastCursor
      });

      if (params.newEntryId) {
        showNotificationTemplate(params.newEntryId);
      }

      if (!objectSubTypeId && response.data.length > 0) {
        history.push(`${baseUrl}/${response.data[0]._id}`)
      }
    }

    setNotificationsParams(null);
  }, [id, objectSubTypeId, baseUrl, history, showNotificationTemplate]);

  useEffect(() => {
    if (permissions.read && notificationsParams) {
      getNotificationTemplates(notificationsParams);
    }
  }, [permissions.read, notificationsParams, getNotificationTemplates,]);

  useEffect(() => {
    const subscribeId = PubSub.subscribe('refresh-notifications', (params) => {
      setNotificationsParams({
        pageIncrement: 0,
        firstCursor: params?.entryDeleted ? "" : cursors.firstCursor,
        lastCursor: params?.entryDeleted ? "" : cursors.lastCursor,
        newEntryId: params?.newEntryId,
        entryDeleted: params?.entryDeleted,
        getLatest: !!params?.newEntryId
      });
    });

    return () => {
      PubSub.unsubscribe("refresh-notifications", subscribeId);
    };
  }, [cursors.firstCursor, cursors.lastCursor]);

  const previous = useCallback(() => {
    setNotificationsParams({
      pageIncrement: -1,
      firstCursor: cursors.firstCursor,
      lastCursor: cursors.lastCursor
    });
  }, [cursors.firstCursor, cursors.lastCursor]);

  const next = useCallback(() => {
    setNotificationsParams({
      pageIncrement: 1,
      firstCursor: cursors.firstCursor,
      lastCursor: cursors.lastCursor
    });
  }, [cursors.firstCursor, cursors.lastCursor]);

  const onAddNotification = useCallback(() => {
    setShowCreateNotification(true);
  }, []);

  const renderNotificationsList = useMemo(() => {
    return <div className='card'>
      <div className='card-header border-bottom sticky-top bg-white'>
        <Toolbar
          buttons={[
            {
              name: "ChevronLeft",
              title: translate("OTM_PREVIOUS"),
              onClick: previous,
              disabled: currentPage === 1
            },
            {
              name: "ChevronRight",
              title: translate("OTM_NEXT"),
              onClick: next,
              disabled: !hasNextPage
            },
            {
              name: "Add",
              title: translate("OTM_CREATE_NOTIFICATION_TEMPLATE"),
              onClick: onAddNotification,
              disabled: !templatePermissions.create
            }
          ]}
          heading={translate("OTM_NOTIFICATION_TEMPLATE_DETAILS")}
        />
      </div>
      <div className='card-body'>
        <List
          data={data}
          attr='name'
          selectedElement={objectSubTypeId}
          onClick={(eventKey) => history.push(`${baseUrl}/${eventKey}`)}
        />
      </div>
    </div>
  }, [baseUrl, currentPage, data, hasNextPage, history, objectSubTypeId, templatePermissions.create, next, onAddNotification, previous, translate]);

  return (
    <div className='row h-100'>
      <CreateNotificationTemplateModal
        show={showCreateNotification}
        onCancel={() => setShowCreateNotification(false)}
      />
      <div className='col-3 overflow-auto h-100'>
        {renderNotificationsList}
      </div>
      <div className='col-9 overflow-auto h-100'>
        {objectSubTypeId && <NotificationDetails {...data.find(({ _id }) => _id === objectSubTypeId)} userIds={userIds} />}
      </div>
    </div>
  );
};

export default SendNotifications;